/**
 * API Declaration Module
 *
 * This module defines all the API endpoints used throughout the application.
 * It provides a centralized place to manage API calls and promotes reusability.
 *
 * The functions are organized by API resource type and HTTP method:
 * - User and authentication
 * - Funnels and funnel management
 * - Leads and lead management
 * - Analytics and reports
 * - Settings and configuration
 * - Integrations
 *
 * Each function returns a promise from the axios helper methods.
 *
 * @module services/api-declaration
 */

import {
  axiosPost, // POST requests
  axiosFetch, // GET requests
  axiosPatch, // PATCH requests
  axiosDelete, // DELETE requests
  axiosDownload, // File downloads
  axiosGetBuildFile // Special build file getter
} from "./axios-rest-methods";
import authConfig from "../config/auth";

/**
 * ======================================
 * Authentication and User APIs
 * ======================================
 */

/**
 * Get current authenticated user data
 *
 * @returns {Promise} Promise resolving to user data
 */
export const getMe = () => axiosFetch(authConfig.meEndpoint);
export const getMeForSiteSettings = () =>
  axiosFetch(`${authConfig.meEndpoint}/fb-accounts`);

/**
 * Fetch all users with optional filtering
 *
 * @param {Object} params - Query parameters for filtering and pagination
 * @returns {Promise} Promise resolving to list of users
 */
export const fetchAllUsers = (params) => axiosFetch("user", { params });

/**
 * Fetch a single user by ID
 *
 * @param {string} id - User ID to fetch
 * @returns {Promise} Promise resolving to user data
 */
export const fetchSingleUser = (id) => axiosFetch(`user/${id}`);

/**
 * Get application build information file
 * Used for version checking and updates
 *
 * @returns {Promise} Promise resolving to build data
 */
export const getBuildFile = () => axiosGetBuildFile();

/**
 * ======================================
 * Missing Information APIs
 * These endpoints handle identification and management of missing required data
 * ======================================
 */

/**
 * Get count of all missing information by type
 *
 * @param {Object} params - Query parameters
 * @returns {Promise} Promise resolving to counts of missing info
 */
export const fetchMissingInfoCount = (params) =>
  axiosFetch(`missing-info/count`, { params });

/**
 * Fetch leads with missing information
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to leads with missing info
 */
export const fetchMissingLeadsInfo = (params) =>
  axiosFetch(`missing-info/leads`, { params });

/**
 * Fetch categories with missing information
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to categories with missing info
 */
export const fetchMissingCategoryInfo = (params) =>
  axiosFetch(`missing-info/category`, { params });

/**
 * Fetch customers with missing information
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to customers with missing info
 */
export const fetchMissingCustomerInfo = (params) =>
  axiosFetch(`missing-info/customer`, { params });

/**
 * Fetch HubSpot integrations with missing information
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to HubSpot connections with missing info
 */
export const fetchMissingHubspotInfo = (params) =>
  axiosFetch(`missing-info/hubspot`, { params });

/**
 * Fetch ad accounts with missing information
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to ad accounts with missing info
 */
export const fetchMissingAdAccountInfo = (params) =>
  axiosFetch(`missing-info/ad-account`, { params });

/**
 * Fetch leads missing UTM parameters
 *
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Promise resolving to leads missing UTM data
 */
export const fetchLeadsWithMissingUTM = (params) =>
  axiosFetch(`missing-info/missing-utm-parameters`, { params });

/**
 * ======================================
 * Lead and Analytics APIs
 * These endpoints handle lead management and analytics data
 * ======================================
 */

/**
 * Get call insights analytics data
 *
 * @param {Object} queryParams - Query parameters for filtering and date ranges
 * @returns {Promise} Promise resolving to call insights data
 */
export const getCallInsights = (queryParams = {}) =>
  axiosFetch("lead-response/analytics/call-insights", {
    params: queryParams
  });

/**
 * Get email templates for appointment confirmations
 *
 * @returns {Promise} Promise resolving to email templates
 */
export const getAppointmentEmailTemplates = () =>
  axiosFetch("leadservice-panel/email-template");

/**
 * Fetch Facebook ad leads by funnel ID
 *
 * @param {string} funnelId - Funnel ID to get leads for
 * @param {Object} queryParams - Query parameters for filtering
 * @returns {Promise} Promise resolving to leads from Facebook
 */
export const fetchFbAdLeadsByFunnelId = (funnelId, queryParams = {}) =>
  axiosFetch(`lead-response/fb-ad-leads/${funnelId}`, {
    params: queryParams
  });

/**
 * Get lead synchronization rate analytics
 *
 * @param {Object} queryParams - Query parameters for filtering and date ranges
 * @returns {Promise} Promise resolving to lead sync rate data
 */
export const getLeadSyncRate = (queryParams = {}) =>
  axiosFetch(`lead-response/analytics/lead-sync-rate`, {
    params: queryParams
  });

/**
 * Fulfillment KPI dashboard: monthly KPI values (ADMIN_TIER)
 *
 * @param {Object} queryParams - Optional `from` / `to` ("YYYY-MM")
 * @returns {Promise} Promise resolving to {thresholds, kpi1, kpi3}
 */
export const getFulfillmentKpiOverview = (queryParams = {}) =>
  axiosFetch(`fulfillment-kpi/overview`, { params: queryParams });

/**
 * Fulfillment KPI dashboard: new customers without a first lead
 *
 * @param {Object} queryParams - Optional `minDays` (1–30, default 20)
 * @returns {Promise} Promise resolving to the customer rows
 */
export const getFulfillmentKpiWithoutStart = (queryParams = {}) =>
  axiosFetch(`fulfillment-kpi/new-customers-without-start`, {
    params: queryParams
  });

/**
 * Fulfillment KPI dashboard: campaign start within 30 days from HubSpot
 *
 * @param {Object} queryParams - Optional `from` / `to` ("YYYY-MM"), `minDays`
 * @returns {Promise} Promise resolving to {available, rows, withoutStart} or {available: false, reason}
 */
export const getFulfillmentKpiCampaignStart = (queryParams = {}) =>
  axiosFetch(`fulfillment-kpi/campaign-start`, { params: queryParams });

/**
 * Fulfillment KPI dashboard: customers below the lead threshold this month
 *
 * @param {Object} queryParams - Optional `threshold` (10 or 12)
 * @returns {Promise} Promise resolving to the customer rows
 */
export const getFulfillmentKpiBelowThreshold = (queryParams = {}) =>
  axiosFetch(`fulfillment-kpi/customers-below-threshold`, {
    params: queryParams
  });

export const getAppointmentConfirmationAnalysis = (queryParams = {}) =>
  axiosFetch(`lead-response/analytics/appointments`, {
    params: queryParams
  });

export const getAllFunnels = async (queryParams = {}) =>
  axiosFetch("funnel", { params: queryParams });

export const getFunnelQuestion = (funnelId, questionId) =>
  axiosFetch(`funnel/${funnelId}/question/${questionId}`);

export const getAllFunnelsForSearch = async (queryParams = {}) =>
  axiosFetch("funnel/search", { params: queryParams });

export const getAllFunnelsEvaluation = async (queryParams = {}) =>
  axiosFetch("funnels-evaluation", { params: queryParams });

export const getAllAdCreativesEvaluation = async (queryParams = {}) =>
  axiosFetch("ads-evaluation", { params: queryParams });

export const getAdCreativeInsights = async (adId) =>
  axiosFetch(`ads-evaluation/creative-insights/${adId}`);

//TODO: Verify before removing it.
export const fetchCampaignOptimization = async (queryParams = {}) =>
  axiosFetch("funnels-evaluation/optimization", { params: queryParams });

export const fetchBlockedZipTasks = async (queryParams = {}) =>
  axiosFetch("funnels-evaluation/zip-check", { params: queryParams });

export const fetchWebsiteCreationLogs = async (queryParams = {}) =>
  axiosFetch("customer/website/logs", { params: queryParams });

export const getAllCampaignsEvaluation = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnels-evaluation/${funnelId}`, { params: queryParams });

export const getAllAdsetsEvaluation = async (
  // funnelId,
  campaignId,
  queryParams = {}
) => {
  // `funnels-evaluation/${funnelId}/${campaignId}`,
  return axiosFetch(`funnels-evaluation/${campaignId}`, {
    params: queryParams
  });
};
export const getAllAdsEvaluation = async (
  // funnelId,
  campaignId,
  adsetId,
  queryParams = {}
) =>
  axiosFetch(`funnels-evaluation/${campaignId}/${adsetId}`, {
    params: queryParams
  });

export const getAllFunnelsEvaluationInsights = async (queryParams = {}) =>
  axiosFetch("funnels-evaluation/insights", {
    params: queryParams
  });

export const getAllTemplates = async (queryParams = {}) =>
  axiosFetch("template", { params: queryParams });

export const getFunnelById = async (funnelId) =>
  axiosFetch(`funnel/${funnelId}`);

export const getFunnelVersionsById = async (funnelId) =>
  axiosFetch(`funnel/${funnelId}/versions`);

export const getTemplateById = async (templateId) =>
  axiosFetch(`template/${templateId}`);

export const getFunnelSiteInfo = async (queryParams) =>
  axiosFetch("funnel/site", { params: queryParams });

export const fetchFunnelInsights = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnel-insight/${funnelId}`, {
    params: queryParams
  });

export const fetchBlockedPostalCodes = async (funnelId) =>
  axiosFetch(`blocked-postal-code/${funnelId}`);

export const fetchBlockedPostalCodesCount = async (funnelId) =>
  axiosFetch(`blocked-postal-code/count/${funnelId}`);

export const fetchFunnelInsightVersions = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnel-insight/${funnelId}/versions`, {
    params: queryParams
  });

export const fetchLeadResponseInsight = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnel-insight/${funnelId}/lead-details`, {
    params: queryParams
  });

export const fetchLeadServiceInsight = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnel-insight/${funnelId}/lead-service`, {
    params: queryParams
  });

export const fetchInterviewInsight = async (funnelId, queryParams = {}) =>
  axiosFetch(`funnel-insight/${funnelId}/interview`, {
    params: queryParams
  });

// Split tests on the funnel order form (Asana 1219217770567617)
export const fetchSplitTestOverview = async (queryParams = {}) =>
  axiosFetch("split-test/overview", { params: queryParams });

export const fetchSplitTestBaseline = async (queryParams = {}) =>
  axiosFetch("split-test/baseline", { params: queryParams });

export const fetchSplitTest = async (id) => axiosFetch(`split-test/${id}`);

export const proposeSplitTest = async (body) =>
  axiosPost("split-test/propose", body);

export const createSplitTest = async (body) => axiosPost("split-test", body);

export const updateSplitTest = async (id, body) =>
  axiosPatch(`split-test/${id}`, body);

export const startSplitTest = async (id) =>
  axiosPost(`split-test/${id}/start`, {});

export const cancelSplitTest = async (id) =>
  axiosPost(`split-test/${id}/cancel`, {});

export const evaluateSplitTest = async (id) =>
  axiosPost(`split-test/${id}/evaluate`, {});

export const rolloutSplitTest = async (id) =>
  axiosPost(`split-test/${id}/rollout`, {});

export const revertSplitTest = async (id) =>
  axiosPost(`split-test/${id}/revert`, {});

// Global defaults of the order form per funnel type (split-test elements v2)
export const fetchFormDefaults = async () =>
  axiosFetch("workspace/form-defaults");

export const updateFormDefaults = async (body) =>
  axiosPatch("workspace/form-defaults", body);

// includeHidden is only honoured by the backend for super admins
export const fetchCountryOptions = async (params = {}) =>
  axiosFetch("country-options", { params });

export const createCountryOption = async (body) =>
  axiosPost("country-options", body);

export const updateCountryOption = async (id, body) =>
  axiosPatch(`country-options/${id}`, body);

export const getAllCustomers = async (queryParams = {}) =>
  axiosFetch("customer", { params: queryParams });

export const getCustomerById = async (customerId, isHubspotTabSelected) =>
  axiosFetch(`customer/${customerId}`, {
    params: {
      isHubspotCustomer: isHubspotTabSelected
    }
  });

export const getFunnelsReport = async (params = {}) =>
  axiosFetch("funnel/performance-reports", { params });

export const getGroupedCustomerFunnelReport = async (customerId) =>
  await axiosFetch(`customer/grouped-funnel-type-report/${customerId}`);

export const getCustomerPerformanceReport = async (
  customerId,
  queryParams = {}
) =>
  axiosFetch(`customer/funnel-report/${customerId}`, {
    params: queryParams
  });

export const getCustomerInvoices = async (customerId, queryParams = {}) =>
  axiosFetch(`customer/invoices/${customerId}`, {
    params: queryParams
  });

export const getCustInvoicesYears = async (customerId, queryParams = {}) =>
  axiosFetch(`customer/invoices-year/${customerId}`, {
    params: queryParams
  });

export const getInvoicePdf = async (invoiceId, queryParams = {}) =>
  axiosFetch(`customer/invoice/${invoiceId}/pdf`, {
    params: queryParams,
    responseType: "blob"
  });

export const getCustomerReportByFunnelId = async (
  customerId,
  funnelId,
  queryParams = {}
) =>
  axiosFetch(`customer/single-funnel-report/${customerId}/${funnelId}`, {
    params: queryParams
  });

export const getAllLeads = async (queryParams = {}) =>
  axiosFetch("lead-response", { params: queryParams });

export const getAllPartialLeads = async (params = {}) =>
  axiosFetch("upcoming-lead-info", { params });

export const assignLeadToAgent = (params = {}) =>
  axiosFetch("lead-response/lead-assignment-to-agent", { params });

// A&M Autopilot: state, free slots and booking of a lead the Autopilot
// handed to the Lead Service (Asana 1219547818089406)
export const getAutopilotState = (leadId) =>
  axiosFetch(`lead-response/${leadId}/autopilot`);

export const getAutopilotSlots = (leadId) =>
  axiosFetch(`lead-response/${leadId}/autopilot/slots`);

export const bookAutopilotAppointment = (leadId, body) =>
  axiosPost(`lead-response/${leadId}/autopilot/booking`, body);

export const getLeadsBySearch = (params = {}) =>
  axiosFetch("lead-response/lead-agent-search", { params });

export const getFunnelsForLeadsBySearch = (params = {}) =>
  axiosFetch("lead-response/lead-agent-funnel-search", { params });

export const getCustomerByEmail = async (queryParams) =>
  axiosFetch("customer/availability", {
    params: queryParams
  });

export const getAllCategories = async (queryParams = {}) =>
  axiosFetch("categories", { params: queryParams });

export const getCategoryByName = async (queryParams) =>
  axiosFetch("categories/availability", {
    params: queryParams
  });

export const getCategoryById = async (categoryId) =>
  axiosFetch(`categories/${categoryId}`);

export const getAllCities = async (queryParams = {}) =>
  axiosFetch("city", { params: queryParams });

export const getDealStages = async (queryParams = {}) =>
  axiosFetch("deal-stages", { params: queryParams });

export const getFbPages = async (config) =>
  axiosFetch("facebook/pages", config);

export const getAllAssets = async (params = {}) =>
  axiosFetch("asset", { params });

export const getSingleAsset = (assetId) => axiosFetch(`asset/${assetId}`);

export const getAllTags = async (params = {}) => axiosFetch("tag", { params });

export const getSingleTag = async (id) => axiosFetch(`tag/${id}`);

export const getTagByName = async (queryParams) =>
  axiosFetch("tag/availability", {
    params: queryParams
  });

export const getBasicTags = async () => axiosFetch("tag/basic");

export const getFbForms = async (pageId, config) =>
  axiosFetch(`facebook/forms/${pageId}`, config);

export const getLeadById = async (leadId) =>
  axiosFetch(`lead-response/${leadId}`);

export const getUpcomingLeadById = async (upcomingLeadId) =>
  await axiosFetch(`upcoming-lead-info/${upcomingLeadId}`);

export const getIntegrationsByFunnelId = async (funnelId) =>
  axiosFetch(`integrations/${funnelId}`);

export const getActiveCCIntegration = async (funnelId, queryParams = {}) =>
  axiosFetch(`integrations/contact-cloud-integration/${funnelId}`, {
    params: queryParams
  });

export const getAfterConfirmationIntegrationIdsByFunnel = async (funnelId) =>
  axiosFetch(`integrations/${funnelId}/after-confirmation-integrations`);

export const getContactCloudProducts = async (tenantId) =>
  axiosFetch(`integrations/contact-cloud-products/${tenantId}`);

export const getContactCloudUsers = async (tenantId) =>
  axiosFetch(`integrations/contact-cloud-users/${tenantId}`);

export const getContactCloudJobs = async (tenantId) =>
  axiosFetch(`integrations/contact-cloud-jobs/${tenantId}`);

export const getLeadActivities = async (leadId) =>
  axiosFetch(`activity/${leadId}`);

export const getLeadPrice = async (categoryId) =>
  axiosFetch(`categories/${categoryId}/get-lead-price`);

export const getSuggestions = (queryParams = {}) =>
  axiosFetch("suggestion-gallery", {
    params: queryParams
  });

export const getOnHoldLeadsCount = (queryParams = {}) =>
  axiosFetch("lead-response/on-hold-leads-count", {
    params: queryParams
  });

export const getLatestSystemHealthError = async () =>
  axiosFetch("system-health-alert/get-latest");

export const getSystemHealthErrorByApi = async (params = {}) =>
  axiosFetch("system-health-alert/get-error-by-api", { params });

export const getNonRejectedRawHookData = async () =>
  axiosFetch("fb-hook-raw-data/non-rejected");

export const getFunnelLandingPage = (params = {}) =>
  axiosFetch("landing-page", { params });

export const getFunnelsCount = (params = {}) =>
  axiosFetch("funnel/get-count", { params });

export const getLeadsCount = (params = {}) =>
  axiosFetch("lead-response/get-count", { params });

export const getAdDetail = (adId) =>
  axiosFetch(`lead-response/ad-detail/${adId}`);

export const getFunnelBasedCampaignsReview = async (
  funnelId,
  queryParams = {}
) =>
  axiosFetch(`lead-response/campaigns-review/${funnelId}`, {
    params: queryParams
  });

export const getLeadServiceGlobalInsights = (params = {}, signal) =>
  axiosFetch("lead-response/lead-service-global-insights", {
    params,
    signal
  });

export const fetchFunnelNotSuitableCCLeads = async (
  funnelId,
  queryParams = {}
) =>
  axiosFetch(`lead-response/not-suitable-cc-leads/${funnelId}`, {
    params: queryParams
  });

export const getAllKeywords = async (queryParams = {}) =>
  axiosFetch("keywords", { params: queryParams });

export const getKeywordByName = async (queryParams) =>
  axiosFetch("keywords/availability", { params: queryParams });

export const getKeywordById = async (keywordId) =>
  axiosFetch(`keywords/${keywordId}`);

export const getCustomerAllWebTemplates = async (queryParams = {}) =>
  axiosFetch("customer/get-all-templates", {
    params: queryParams
  });

export const getCustomerTemplateSections = async (queryParams = {}) =>
  axiosFetch("customer/template/sections", {
    params: queryParams
  });

export const getPixabayImages = async (queryParams = {}) =>
  axiosFetch("customer/pixabay-images", {
    params: queryParams
  });

export const getCustomerActiveWebsiteTemplate = async (queryParams = {}) =>
  axiosFetch("customer/get-active-template", { params: queryParams });

export const getCustomerWebsiteInfo = async (queryParams = {}) =>
  axiosFetch("customer/get-template", {
    params: queryParams
  });

export const getDnsRecord = async (queryParams = {}) =>
  axiosFetch("customer/website/dns-record", {
    params: queryParams
  });

export const fetchWebsiteLocalSeosByCustomer = async ({
  customerId,
  tabRoute
}) => axiosFetch(`local-seo/${customerId}`, { params: { tabRoute } });

export const getCustomerFunnelCategories = async (customerId, params) =>
  axiosFetch(`customer/${customerId}/website/categories`, {
    params
  });

export const fetchCustomerWebsiteAnalytics = async (queryParams = {}) =>
  axiosFetch("customer-website/analytics", { params: queryParams });

export const fetchWebsiteFunnels = async (websiteId) =>
  axiosFetch(`websites/${websiteId}/funnels`);

export const fetchWebsiteFunnelOptions = async (websiteId, queryParams = {}) =>
  axiosFetch(`websites/${websiteId}/funnel-options`, {
    params: queryParams
  });

export const saveWebsiteFunnels = async (websiteId, payload) =>
  axiosPost(`websites/${websiteId}/funnels`, payload);

export const deleteWebsiteFunnel = async (websiteId, funnelId) =>
  axiosDelete(`websites/${websiteId}/funnels/${funnelId}`);

export const fetchCustomerWebsiteVersions = async ({ customerId, tabRoute }) =>
  axiosFetch(`customer-website/${customerId}/versions`, {
    params: { tabRoute }
  });

export const fetchCustomerActiveWebVersion = async ({ customerId, tabRoute }) =>
  axiosFetch(`customer-website/${customerId}/active-version`, {
    params: { tabRoute }
  });

export const getCustomerPublishedWebsiteCount = async ({ customerId }) =>
  axiosFetch(`customer/${customerId}/website/published-count`);

export const getCustomerBaseWebsiteFavIcon = async ({ customerId }) =>
  axiosFetch(`customer/${customerId}/website/base-fav-icon`);

export const getCustomerCity = async ({ customerId }) =>
  axiosFetch(`customer/${customerId}/fetch-city`);

export const fetchCitySubDistricts = async ({ city }) =>
  axiosFetch("local-seo/city-sub-districts", { params: { city } });

export const updatePublicSeoSite = async ({ customerId }) =>
  axiosFetch(`local-seo/${customerId}/update-public-seo`);

// params.active = true: active tabs only
export const getWebsitesForTabs = async (customerId, params) =>
  axiosFetch(`customer/${customerId}/get-websites-tabs`, { params });

export const getWebsiteDomainByCustomerId = async (customerId) =>
  axiosFetch(`customer-website-domains/${customerId}`);

export const getAMPrivacyPolicy = () =>
  axiosFetch("workspace/get-am-privacy-policy");

export const getNextOfferFollowupLead = (params = {}) =>
  axiosFetch("offer-followup/next", { params });

export const getOfferFollowupLeadsBySearch = (params = {}) =>
  axiosFetch("offer-followup/search", { params });

// "DOWNLOAD" Requests
export const downloadLeadsAsCsv = async (queryParams = {}) =>
  axiosDownload({ url: "lead-response/download", queryParams });

// export const exportNinetyDaysNotSuitableLeads = async () => {
//   return axiosDownload({
//     url: "funnels-evaluation/export-not-suitable-leads"
//   });
// };

// "POST" Requests
export const loginUser = async (body) =>
  axiosPost(authConfig.loginEndpoint, body);

export const generateOTP = () => axiosPost("user/otp/generate/authenticator");

export const generateOTPViaService = (payload) =>
  axiosPost("user/otp/generate/email", payload);

export const setupAuthenticator = (payload) =>
  axiosPost("user/otp/setup/authenticator", payload);

export const setupEmailVerification = (payload) =>
  axiosPost("user/otp/setup/email", payload);

export const createUser = (body) => axiosPost("user", body);

// export const assignUserToken = (body) => {
//   return axiosPost(`site-settings`, body);
// };
export const changepassword = (body) => axiosPost("user/change-password", body);

export const createActivity = (body, id) => axiosPost(`activity/${id}`, body);

export const startOfferFollowupCall = (id, payload) =>
  axiosPost(`offer-followup/${id}/start`, payload);

export const completeOfferFollowupLead = (id, payload) =>
  axiosPost(`offer-followup/${id}/complete`, payload);

export const releaseOfferFollowupLead = (id) =>
  axiosPost(`offer-followup/${id}/release`);

export const resetPasswordToken = async (body) =>
  axiosPost("user/reset-password-token", body);

export const resetPassword = async (body) =>
  axiosPost("user/reset-password", body);

export const createDraftFunnel = async () => axiosPost("funnel/draft", {});

export const createDraftTemplate = async () => axiosPost("template/draft", {});

export const uploadFunnelAssets = async (payload) =>
  axiosPost("funnel/upload-assets", payload);

export const changeVersionById = async (funnelId, payload) =>
  axiosPost(`funnel/${funnelId}/change-version`, payload);

export const integrateFunnel = async (funnelId, payload) =>
  axiosPost(`integrations/${funnelId}`, payload);

export const createAssetEntry = async (payload) => axiosPost("asset", payload);

export const createCustomer = async (payload) => axiosPost("customer", payload);

export const createCustomerWebTemplate = async (customerId, payload) =>
  axiosPost(`customer/${customerId}/create-template`, payload);

export const generateWebsiteContent = async (payload) =>
  axiosPost(`customer/website/generate-content`, payload);

export const saveLocalSeos = async ({
  customerId,
  tabRoute,
  cities,
  keywords
}) => axiosPost(`local-seo/${customerId}`, { tabRoute, cities, keywords });

export const generateLocalSeoContent = async ({
  customerId,
  city,
  keyword,
  subPageId,
  parentCity
}) =>
  axiosPost(`local-seo/${customerId}/generate-content`, {
    city,
    keyword,
    subPageId,
    parentCity
  });

export const regenerateCustomerWebsite = async (logId) =>
  axiosPost(`customer/website/regenerate/${logId}`);

export const generateWebsiteImages = async (payload) =>
  await axiosPost(`customer/website/generate-images`, payload);

export const duplicateFunnel = async (funnelId, payload) =>
  axiosPost(`funnel/${funnelId}/duplicate`, payload);

export const createCategory = async (payload) =>
  axiosPost("categories", payload);

export const syncManually = async (payload) =>
  axiosPost("lead-response/sync", payload);

export const createCity = async (payload) => axiosPost("city", payload);

export const loadToken = async (payload) =>
  axiosPost("facebook/update-token", payload);

export const loadClickfunnelHtml = async (importUrl) =>
  axiosPost(
    `funnel/load-clickfunnel-html`,
    { url: importUrl },
    { responseType: "document" }
  );

export const createTag = (payload) => axiosPost("tag", payload);

export const isAdValid = (adId) =>
  axiosPost(`facebook/check-ad-validity/${adId}`);

export const createAdCopies = (payload) => axiosPost("ad-copy", payload);

export const validateContactCloudId = (payload) =>
  axiosPost("customer/validate-contact-cloud-id", payload);

export const upoadImageToFacebook = (adAccountId, formData, config) =>
  axiosPost(`facebook/facebook-upload-image/${adAccountId}`, formData, config);

export const duplicateAd = (payload) =>
  axiosPost(`facebook/duplicate-ad`, payload);

export const updateMissingCategoryInfo = (payload) =>
  axiosPost(`missing-info/category`, payload);

export const updateMissingCustomerInfo = (payload) =>
  axiosPost(`missing-info/customer`, payload);

export const updateMissingHubspotInfo = (payload) =>
  axiosPost(`missing-info/hubspot`, payload);

export const updateMissingAdAccountInfo = (payload) =>
  axiosPost(`missing-info/ad-account`, payload);

export const updateMissingLeadInfo = (payload) =>
  axiosPost(`missing-info/leads`, payload);

export const uploadSuggestion = (payload) =>
  axiosPost("suggestion-gallery", payload);

export const createFunnelLandingPage = async (payload) =>
  axiosPost("landing-page", payload);

export const sendInvoiceToCustViaEmail = (customerId, invoiceId, payload) =>
  axiosPost(`customer/${customerId}/send-email-invoice/${invoiceId}`, payload);

export const createKeyword = async (payload) => axiosPost("keywords", payload);

// "PATCH" Requests
export const updateAsset = async (payload, assetId) =>
  axiosPatch(`asset/${assetId}`, payload);

export const updateLeadById = (id, body) =>
  axiosPatch(`lead-response/${id}`, body);

export const updateLeadAdAssociation = (id, action, body) =>
  axiosPatch(`lead-response/${id}/update-ad-association/${action}`, body);

export const saveFbForm = async (funnelId, payload) =>
  axiosPatch(`funnel/save-fb-form/${funnelId}`, payload);

// export const updateUserToken = (userId, body) => {
//   return axiosPatch(`site-settings/${userId}`, body);
// };
export const setPrimaryToken = (userId) =>
  axiosPatch(`site-settings/${userId}/primary`);

export const updateFunnel = async (funnelId, payload) =>
  axiosPatch(`funnel/${funnelId}`, payload);

export const updateFunnelFilters = async (funnelId, payload) =>
  axiosPatch(`funnel/${funnelId}/filter`, payload);

export const updateFunnelLandingPage = async (funnelId, payload) =>
  axiosPatch(`funnel/${funnelId}/update-landing-page-values`, payload);

export const toggleLandingPageEnable = async (funnelId, payload) =>
  axiosPatch(`funnel/${funnelId}/toggle-landing-page-enable`, payload);

export const handleLandingPageForVersionChange = async (funnelId, payload) =>
  axiosPatch(
    `funnel/${funnelId}/update-landing-page-on-version-change`,
    payload
  );

export const updateTemplate = async (templateId, payload) =>
  axiosPatch(`template/${templateId}`, payload);

export const archiveFunnel = async (funnelId) =>
  axiosPatch(`funnel/${funnelId}/archive`);

export const unarchiveFunnel = async (funnelId, payload) =>
  axiosPatch(`funnel/${funnelId}/unarchive`, payload);

export const archiveLead = async (leadId, payload) =>
  axiosPatch(`lead-response/${leadId}/archive`, payload);

export const updateCustomer = async (customerId, payload) =>
  axiosPatch(`customer/${customerId}`, payload);

export const updateCategory = async (categoryId, payload) =>
  axiosPatch(`categories/${categoryId}`, payload);

export const updateIntegration = async (integrationId, payload) =>
  axiosPatch(`integrations/${integrationId}`, payload);

export const updateUserById = (body, id) => axiosPatch(`user/${id}`, body);

export const updateUserFromProfileSettings = (body) =>
  axiosPatch(`user/me/update`, body);

export const toggleLeadService = (body, id) =>
  axiosPatch(`funnel/${id}/toggle-lead-service`, body);

export const toggleLeadServicePlus = (body, id) =>
  axiosPatch(`funnel/${id}/toggle-lead-service-plus`, body);

export const toggleAlternativeAddress = (body, id) =>
  axiosPatch(`funnel/${id}/toggle-alternative-address`, body);

export const updateAlternativeAddress = (body, id) =>
  axiosPatch(`funnel/${id}/update-alternative-address`, body);

export const handleCustomerAppointmentLink = (id, body) =>
  axiosPatch(`funnel/${id}/appointment-link`, body);

export const updateTag = (id, payload) => axiosPatch(`tag/${id}`, payload);

export const updateIntegrations = ({ funnelId, integrationType }, payload) =>
  axiosPatch(`integrations/${funnelId}/${integrationType}`, payload);

export const updateLeadBlockTime = (payload) =>
  axiosPatch(`lead-response/lead-block-time`, payload);

export const resetLead = (leadId, payload = {}) =>
  axiosPatch(`lead-response/reset/${leadId}`, payload);

export const removeLeadForFurtherCalls = (leadId) =>
  axiosPatch(`lead-response/remove-leadservice-manually/${leadId}`);

export const updateCompanyNameForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-company-name-for-agent`, body);

export const updateProductNameForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-product-name-for-agent`, body);

export const updatePricePerInvoiceUnitForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-price-for-agent`, body);

export const updateFixedPricePerInvoiceUnitForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-fixed-price-for-agent`, body);

export const updateNameOfAccountingUniForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-unit-for-agent`, body);

export const updateBudgetAreaOfQueryBoundsForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-budget-area-bounds-for-agent`, body);

export const updateBudgetQueriesForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-budget-queries-for-agent`, body);

export const updatePricesPerAreaForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-prices-per-area-for-agent`, body);

export const updateLeadServicePlusPriceTypeForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-price-type-for-agent`, body);

export const updateLeadServiceQuestionForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-questions-for-agent`, body);

export const updateCampaignOptimizationTask = (id, body) =>
  axiosPatch(`funnels-evaluation/optimization/${id}`, body);

export const updateBlockedZipTask = (id, body) =>
  axiosPatch(`funnels-evaluation/zip-check/${id}`, body);

export const validateAndUpdateLeadAgentInfo = (leadId, payload = {}) =>
  axiosPatch(`lead-response/${leadId}/validate-and-update-agent`, payload);

export const takeoverAiLeadserviceLead = (leadId) =>
  axiosPatch(`lead-response/${leadId}/ai-leadservice-manual-takeover`);

export const ignoreMissingLeadInfo = (payload) =>
  axiosPatch("missing-info/leads/ignore", payload);

export const updateSuggestion = (id, payload) =>
  axiosPatch(`suggestion-gallery/${id}`, payload);

export const archiveOrUnarchiveMultipleSuggestions = (payload) =>
  axiosPatch(
    `suggestion-gallery/${
      payload.shouldArchive ? "archive" : "unarchive"
    }-multiple`,
    payload
  );

export const archiveOrUnarchiveSuggestion = (id, payload) =>
  axiosPatch(
    `suggestion-gallery/${id}/${
      payload.shouldArchive ? "archive" : "unarchive"
    }`,
    payload
  );

export const setIsWinningSuggestion = (id, payload) =>
  axiosPatch(`suggestion-gallery/${id}/is-winning`, payload);

export const handleAdvertisingAccountLink = (id, body) =>
  axiosPatch(`funnel/${id}/advertising-account-link`, body);

export const changeAgentScript = (payload) =>
  axiosPatch("leadservice-panel/change-script", payload);

export const syncNowOnDisableLeadservice = (integrationIds) =>
  axiosPatch("lead-response/sync-now-on-disable-leadservice", {
    integrationIds
  });

export const updateVersionLandingPageId = async (versionId, payload) =>
  axiosPatch(`versions/${versionId}/update-landing-page-id`, payload);

export const updateAppointmentEmailTemplates = async (payload) =>
  axiosPatch("leadservice-panel/email-template", payload);

export const updateSystemCallingDownTimeInMinutes = async (payload) =>
  axiosPatch("leadservice-panel/system-calling-down-time", payload);

export const updateSystemCallingDownTimeWebhook = async (payload) =>
  axiosPatch("leadservice-panel/system-calling-down-time-webhook", payload);

// Leadservice daily report: Slack webhook (status only, never the URL)
export const getLeadserviceReportWebhook = async () =>
  axiosFetch("leadservice-panel/leadservice-report-webhook");

export const updateLeadserviceReportWebhook = async (payload) =>
  axiosPatch("leadservice-panel/leadservice-report-webhook", payload);

// Posts a [TEST] message without mentions; does not block the 18:00 post
export const sendLeadserviceReportTest = async () =>
  axiosPost("leadservice-panel/daily-report", {
    dryRun: false,
    testPost: true
  });

export const updateOfferFollowupMaxNotReachedAttempts = async (payload) =>
  axiosPatch(
    "leadservice-panel/offer-followup-max-not-reached-attempts",
    payload
  );

export const updateAiLeadserviceEnabled = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-enabled", payload);

export const updateAiLeadserviceWebhook = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-webhook", payload);

export const updateAiLeadserviceContactAttemptTrigger = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-trigger", payload);

// AI Leadservice - LEAD (Neukunden)
export const updateAiLeadserviceLeadEnabled = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-lead-enabled", payload);

export const updateAiLeadserviceLeadWebhook = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-lead-webhook", payload);

export const updateAiLeadserviceLeadTrigger = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-lead-trigger", payload);

// AI Leadservice - EMPLOYEE (Mitarbeitergewinnung)
export const updateAiLeadserviceEmployeeEnabled = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-employee-enabled", payload);

export const updateAiLeadserviceEmployeeWebhook = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-employee-webhook", payload);

export const updateAiLeadserviceEmployeeTrigger = async (payload) =>
  axiosPatch("leadservice-panel/ai-leadservice-employee-trigger", payload);

export const updateLeadserviceIndividualNoteForAgent = (funnelId, body) =>
  axiosPatch(`funnel/${funnelId}/update-individual-note-for-agent`, body);

export const updateKeyword = async (keywordId, payload) =>
  axiosPatch(`keywords/${keywordId}`, payload);

export const updateLocalSeo = async (seoId, payload) =>
  axiosPatch(`local-seo/${seoId}`, payload);

export const updateAMPrivacyPolicy = ({ amPolicyData }) =>
  axiosPatch("workspace/update-global-privacy-policy", {
    amPolicyData
  });

export const updateGlobalReclaimConfig = ({ globalReclaimConfig }) =>
  axiosPatch("workspace/update-global-reclaim-config", {
    globalReclaimConfig
  });

export const updateDigitalThankyouPage = (payload) =>
  axiosPatch("workspace/update-digital-thankyou-page", {
    payload
  });

export const replaceAsset = async (assetId, payload) =>
  axiosPatch(`asset/replace/${assetId}`, payload);

export const duplicateCustomerWebTemplate = async (customerId, payload) =>
  axiosPatch(`customer/${customerId}/duplicate-template`, payload);

export const moveHubspotCustomer = async (customerId) =>
  axiosPatch(`customer/${customerId}/move`);

export const saveCustomerWebTemplate = async (customerId, payload) =>
  axiosPatch(`customer/${customerId}/save-template`, payload);

export const saveCustomerWebDomain = async (customerId, payload) =>
  axiosPatch(`customer/${customerId}/save-website-domain`, payload);

export const updateFunnelReclaimModeSettings = ({ id, body }) =>
  axiosPatch(`funnel/${id}/update-reclaim-settings`, body);

export const unassignLeadFromAgent = (leadId) =>
  axiosPatch(`lead-response/${leadId}/unassign-agent`);

export const validateAndUpdateOfferFollowupAgentInfo = (id, payload = {}) =>
  axiosPatch(`offer-followup/${id}/validate-and-update-agent`, payload);

// "DELETE Requests"
export const disableAuthenticatorVerification = (payload) =>
  axiosDelete("user/otp/setup/authenticator", payload);

export const disableEmailVerification = (payload) =>
  axiosDelete("user/otp/setup/email", payload);

export const deleteTemplate = async (templateId) =>
  axiosDelete(`template/${templateId}`);

export const deleteAdImage = async (adAccountId, hash) =>
  await axiosDelete(`facebook/delete-image/${adAccountId}/${hash}`);

export const removeIntegration = async (integrationId) =>
  axiosDelete(`integrations/${integrationId}`);

export const deleteCategory = (id) => axiosDelete(`categories/${id}`);

export const deleteUpcomingLeadById = async (upcomingLeadId) =>
  await axiosDelete(`upcoming-lead-info/${upcomingLeadId}`);

export const deleteFavIconAsset = async (assetId, payload = {}) =>
  axiosDelete(`asset/favicon/${assetId}`, payload);

export const deleteKeyword = (id) => axiosDelete(`keywords/${id}`);

export const deleteLocalSeoById = (seoId) => axiosDelete(`local-seo/${seoId}`);

export const deleteLocalSeo = (queryParams = {}) =>
  axiosDelete("local-seo", undefined, queryParams);

export const deleteTemplateDraft = async ({ customerId, tabRoute }) =>
  axiosDelete(`customer/${customerId}/delete-draft`, undefined, {
    tabRoute
  });

// export const deleteUserToken = (id) => {
//   return axiosDelete(`site-settings/${id}`);
// };
